import React from 'react'
import { Box, Typography, TextField, Button } from '@mui/material'

/**
 * Optional date-range narrowing for the WebMap filter overlays. Sits below
 * the existing Year multiselect — the year picker still drives the fetch
 * (so the backend contract is unchanged), and these two fields narrow the
 * already-fetched rows by a continuous date range.
 *
 * Both inputs are optional; setting only one is valid (open-ended range).
 * Clearing both restores the year-only behavior.
 */
export default function DateRangeFields({
    dateRangeStart, setDateRangeStart,
    dateRangeEnd, setDateRangeEnd,
}) {
    const hasAny = Boolean(dateRangeStart) || Boolean(dateRangeEnd)

    const inputSx = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
            height: '36px',
            '& fieldset': {
                borderColor: 'rgba(184, 216, 208, 0.3)',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#20B2AA',
                borderWidth: '1.5px',
                boxShadow: '0 0 0 2px rgba(184, 216, 208, 0.2)',
            },
        },
        '& .MuiInputBase-input': {
            fontSize: '12px',
            color: '#FFF5E6',
            // Surface the native calendar picker icon as cream so it doesn't
            // disappear into the charcoal field background.
            '&::-webkit-calendar-picker-indicator': {
                filter: 'invert(1) opacity(0.7)',
                cursor: 'pointer',
            },
        },
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.25 }}>
                <Typography
                    sx={{
                        fontFamily: "'Nunito', 'Roboto', sans-serif",
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'rgba(255, 245, 230, 0.7)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    Date Range (optional)
                </Typography>
                {hasAny && (
                    <Button
                        size="small"
                        onClick={() => { setDateRangeStart(''); setDateRangeEnd('') }}
                        sx={{
                            minWidth: 0,
                            padding: '0 4px',
                            fontSize: '11px',
                            textTransform: 'none',
                            color: '#20B2AA',
                            lineHeight: 1.2,
                        }}
                    >
                        Clear
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'grid', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ mb: 0.5, fontSize: '11px', fontWeight: 700, color: 'rgba(255, 245, 230, 0.72)' }}>
                        From
                    </Typography>
                    <TextField
                        type="date"
                        size="small"
                        value={dateRangeStart}
                        onChange={(e) => setDateRangeStart(e.target.value)}
                        inputProps={{ max: dateRangeEnd || undefined, 'aria-label': 'From date' }}
                        sx={inputSx}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ mb: 0.5, fontSize: '11px', fontWeight: 700, color: 'rgba(255, 245, 230, 0.72)' }}>
                        To
                    </Typography>
                    <TextField
                        type="date"
                        size="small"
                        value={dateRangeEnd}
                        onChange={(e) => setDateRangeEnd(e.target.value)}
                        inputProps={{ min: dateRangeStart || undefined, 'aria-label': 'To date' }}
                        sx={inputSx}
                    />
                </Box>
            </Box>
        </Box>
    )
}
